import React, { useEffect, useState } from "react";
import { Container, Row, Placeholder, Image, Col } from "react-bootstrap";
import "./HomePage.css";
import MobileAd from "../Ads/MobileAd";
import TopAd from "../Ads/TopAd";
import { Helmet } from "react-helmet";
import { TeamComplete } from "../../types/model";
import NextEventCard from "./components/NextEventCard";
import HomepageLeaderBoard from "./components/HomepageLeaderBoard";
import HomePageInfoRow from "./components/HomePageInfoRow";
import HomePageFeaturedLayout from "./components/HomePageFeaturedLayout";
import Logo from "../../assets/TRC_Logo_bottom.svg";
import RampageBanner from "../../assets/rampage_fantasy_banner.jpeg";
import RampageBannerMobile from "../../assets/redbull_fantasy_banner_mobile.jpeg";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { RootState } from "../../store/store";
import {
	fetchCurrentSeasonThunk,
	fetchFeaturedBrandTeamThunk,
	fetchFeaturedRiderThunk,
	getGlobalLeaguesThunk,
	getLeaderboardThunk,
} from "../../store/thunks/fantasyThunk";
import { setIsMobile } from "../../store/slices/userSlice";
import SmallLogo from "../common/SmallLogo";
import FivebyNinetyAd from "../Ads/FivebyNinetyAd";
import ThreebyNinetyAd from "../Ads/ThreebyNinetyAd";
import { FaFlagCheckered } from "react-icons/fa";
import { IoGlobeSharp } from "react-icons/io5";
import FantasyTeam from "../Fantasy/Components/FantasyTeam";
import { getTeams } from "../../services/teamService";
import HomePageNewsRow from "./components/HomePageNewsRow";
import FantasyFiltersBar from "../Fantasy/Components/Filters/FantasyFiltersBar";

const HomePage = () => {
	const dispatch = useAppDispatch();
	const {
		selectedLeague,
		selectedEvent,
		eventLoading,
		selectedSeries,
		selectedSport,
		selectedSeason,
		selectedLeaderBoard,
		leaderboardLoading,
		featuredRider,
		featuredBrandTeam,
		featuredRiderDescription,
		featuredBrandTeamDescription,
	} = useAppSelector((state: RootState) => state.fantasy);

	const { isMobile, isLoggedIn } = useAppSelector(
		(state: RootState) => state.user,
	);

	const [teams, setTeams] = useState<TeamComplete[]>([]);
	const [teamLoading, setTeamLoading] = useState(false);

	useEffect(() => {
		dispatch(fetchCurrentSeasonThunk());
	}, []);

	useEffect(() => {
		dispatch(
			fetchFeaturedRiderThunk({
				sportId: selectedSport?._id,
				seriesId: selectedSeries?._id,
			}),
		);
		dispatch(
			fetchFeaturedBrandTeamThunk({
				sportId: selectedSport?._id,
				seriesId: selectedSeries?._id,
			}),
		);
	}, [selectedSport?._id, selectedSeries?._id]);

	useEffect(() => {
		if (selectedEvent && selectedEvent?._id) {
			if (
				selectedEvent?.series?._id === selectedSeries?._id &&
				selectedEvent?.sport?._id === selectedSport?._id &&
				selectedLeaderBoard?.teams &&
				selectedLeaderBoard?.teams.length > 0
			) {
				return;
			}

			dispatch(
				getGlobalLeaguesThunk({
					sportId: selectedEvent?.sport._id ?? "",
					seriesId: selectedEvent?.series._id ?? "",
					seasonId: selectedEvent?.season ?? "",
				}),
			);
		}
	}, [selectedEvent]);

	useEffect(() => {
		if (selectedLeague && selectedLeague?._id) {
			dispatch(getLeaderboardThunk(selectedLeague._id));
		}
	}, [selectedLeague]);

	useEffect(() => {
		const handleResize = () => {
			dispatch(setIsMobile(window.innerWidth <= 768));
		};

		handleResize(); // Initial check
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	useEffect(() => {
		if (selectedSeason?._id) {
			fetchTeams();
		}
	}, [selectedSport, selectedSeries, selectedSeason, isLoggedIn]);

	const fetchTeams = async () => {
		try {
			setTeamLoading(true);
			const params: { sport?: string; series?: string; season: string } = {
				season: selectedSeason?._id ?? "",
			};

			if (selectedSport) params.sport = selectedSport?._id;
			if (selectedSeries) params.series = selectedSeries?._id;

			const res = await getTeams(params);
			setTeams(res.data);
		} catch (error) {
			console.error("Error fetching teams:", error);
			setTeams([]);
		} finally {
			setTeamLoading(false);
		}
	};

	return (
		<Container fluid className="px-0 mx-0">
			<Helmet>
				<title>Homepage - The Race Companion</title>
				<meta
					name="description"
					content="The Race Companion Hompage. Fantasy and news for Enduro, Downhill and Slopestyle mountain biking.  Follow along with Crankworx, UCI Downhill & Enduro, Red Bull Hardline and Red Bull Rampage."
				/>
				<meta name="viewport" content="width=device-width, initial-scale=1" />
			</Helmet>
			{/* <Row className="m-0 p-0">{isMobile ? <MobileAd /> : <TopAd />}</Row> */}
			<Container>
				<FantasyFiltersBar className="pb-3 pt-3" />
				<div className="row pb-5 pt-2">
					{eventLoading ? (
						<div className="col-lg-6 col-12">
							<Placeholder as="div" animation="glow" className="my-5">
								<Placeholder xs={8} bg="light" className="rounded-3 mb-2" />
								<Placeholder
									xs={11}
									className="rounded-3"
									bg="light"
									style={{ height: "13rem" }}
								/>
							</Placeholder>
						</div>
					) : (
						selectedEvent &&
						selectedEvent?._id && (
							<div className="col-lg-6 col-12">
								<NextEventCard event={selectedEvent} />
							</div>
						)
					)}
					<div className="col-md-6 col-12">
						{
							<Container className="mt-3">
								{isMobile ? <ThreebyNinetyAd /> : <FivebyNinetyAd />}
								{teams.length > 0 ? (
									<div className="mt-4">
										<FantasyTeam
											team={teams[0]}
											isLoading={teamLoading}
											showLeagues={true}
											showLeagueSeries={false}
											showLeagueSport={false}
										/>
									</div>
								) : (
									<div className="">
										<Row className="mb-1">
											<Col>
												<div className="text-center text-primary fw-bold fs-3 d-flex flex-row justify-content-center align-items-center">
													<SmallLogo />
													<div className="">LIVE LEADERBOARD</div>
												</div>
											</Col>
										</Row>
										<HomepageLeaderBoard
											leaderboard={selectedLeaderBoard}
											leaderBoardLoading={leaderboardLoading}
										/>
									</div>
								)}
							</Container>
						}
					</div>
				</div>
				<div className="pb-4">
					<HomePageNewsRow />
				</div>
				<div className="py-3">
					<HomePageInfoRow />
				</div>
				{/* Featured Section */}
					<div className="py-5">
						<HomePageFeaturedLayout
							featuredRider={featuredRider}
							featuredBrandTeam={featuredBrandTeam}
							featuredRiderDescription={featuredRiderDescription}
							featuredBrandTeamDescription={featuredBrandTeamDescription}
							selectedSportId={selectedSport?._id}
							selectedSeriesId={selectedSeries?._id}
						/>
					</div>
			</Container>
			<div className="d-flex flex-row justify-content-center">
				<Image
					src={Logo}
					alt="Ad Banner"
					style={{
						maxWidth: "15rem",
						height: "auto",
					}}
					className="rounded-3 my-5"
				/>
			</div>
		</Container>
	);
};

export default HomePage;
